import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAtom } from 'jotai';
import { approvalFilter } from '@/recoil/atom';
import useFavoriteApproval from '@/components/Approval/@hooks/useFavoriteApproval';
import useModal from '@/hooks/useModal';
import IntranetLibraryLabel from '@/components/@IntranetLibrary/Label';
import RHFTextArea from '@/components/HookForm/RHFTextarea';
import { FormGroup } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import {
  StyledCancelButton,
  StyledSubmitButton,
  SubmitContainer,
} from '@/components/Approval/RightContainer/Favorites/styles';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import { t } from 'i18next';
import { useSnackbar } from '@/hooks/global-tools';

type FormType = {
  subject: string;
};

type ModifyItem = {
  title: string;
  id: string;
};

type P = {
  handleClose?: () => void;
  modifyItem?: ModifyItem;
  defaultValue?: string;
};
export const useFavoritesModal = ({
  handleClose,
  modifyItem,
  defaultValue,
}: P = {}) => {
  const isModify = !!modifyItem;
  const favoriteModalRef = useRef<ModalRef>(null);
  const toast = useSnackbar();

  const methods = useForm<FormType>({
    defaultValues: { subject: defaultValue ?? '' },
    mode: 'onChange',
  });

  useEffect(() => {
    modifyItem && methods.setValue('subject', modifyItem.title);
  }, [modifyItem]);

  useEffect(() => {
    defaultValue && methods.setValue('subject', defaultValue);
  }, [defaultValue]);

  const { openModal, closeModal } = useModal(
    favoriteModalRef,
    undefined,
    () => {
      handleClose?.();
    },
  );

  const [filters, setFilters] = useAtom(approvalFilter);
  const { handleSet, handleModify } = useFavoriteApproval();

  const {
    formState: { isValid },
  } = methods;

  const onSubmit = async (obj: FormType) => {
    const org = {
      name: obj.subject,
      description: '',
      params: {
        endAuthoredAt: filters.endAuthoredAt,
        types: filters.types,
        authorPassportIds: filters.authorPassportIds,
        approverPassportIds: filters.approverPassportIds,
        currentApproverPassportIds: filters.currentApproverPassportIds,
        finalApproverPassportIds: filters.finalApproverPassportIds,
        coApproverPassportIds: filters.coApproverPassportIds,
        statuses: filters.statuses,
        requireAllApprovers: filters.requireAllApprovers,
        requireAllCoApprovers: filters.requireAllCoApprovers,
        keyword: filters.keyword,
        searchFields: filters.searchFields,
        filterName: obj.subject,
      },
    };

    try {
      if (modifyItem) {
        await handleModify({ ...org, id: modifyItem?.id });
      } else {
        handleSet(org);
      }
      toast?.show({
        message: t(
          modifyItem
            ? '내 필터 이름이 수정되었습니다.'
            : '내 필터로 저장되었습니다.',
        ),
        severity: 'success',
      });
      closeModal();
    } catch (e) {
      console.error(e);
    }
  };

  const FavoriteModal = (
    <div onClick={(e) => e.stopPropagation()}>
      <Modal
        ref={favoriteModalRef}
        title={t(isModify ? '내 필터 이름 수정' : '내 필터 추가')}
        size={'x-small'}
        handleClose={closeModal}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <FormGroup gapsize={'s'}>
              <IntranetLibraryLabel
                isRequired={true}
                title={t('내 필터 이름')}
                size={14}
              />
              <RHFTextArea
                required={true}
                name={'subject'}
                maxLength={80}
                minRows={6}
                placeholder={t(
                  '내 필터 이름을 등록해 주세요.\n등록한 이름은 언제든 수정할 수 있어요.',
                )}
              />
            </FormGroup>
            <SubmitContainer>
              <StyledCancelButton type="button" onClick={closeModal}>
                {t('취소')}
              </StyledCancelButton>
              <StyledSubmitButton disabled={!isValid}>
                {t('저장')}
              </StyledSubmitButton>
            </SubmitContainer>
          </form>
        </FormProvider>
      </Modal>
    </div>
  );

  return {
    openModal,
    FavoriteModal,
    closeModal,
  };
};
